import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { THEMES, THEME_NAMES, DEFAULT_THEME, migrateThemeName } from "./themes";
import type { ThemeName, ThemeDefinition } from "./themes";

const STORAGE_KEY = "waytide-theme";
export interface GlassSettings {
  opacity: number;     // 0–1, default 0.82
  blur: number;        // px, default 24
}

export interface ShadowSettings {
  opacity: number;     // 0–1, default 0.25
  blur: number;        // px, default 6
  spread: number;      // px, default 2
}

export interface BarSettings {
  // Letterbox bars (cinema-style top/bottom)
  letterboxHeight: number;   // px, default 48 — total height of letterbox bar
  letterboxOpacity: number;  // 0–1, default 0.08
  letterboxBlur: number;     // px, default 2
  letterboxInset: number;    // %, default 3 — horizontal inset from screen edges
  // Separator line (at inner edge of letterbox)
  separatorPx: number;       // px, default 2 — thickness
  separatorFade: number;     // %, default 10 — how much of each end fades to transparent
  // Menu bar (sits on top separator, extends upward)
  menuBarHeight: number;     // px, default 36 — height from separator upward
  menuBarScale: number;      // multiplier, default 1 — scales toolbar icons
  // Global
  cornerRadius: number;      // px, default 12 (panel radius)
}

const DEFAULT_GLASS: GlassSettings = { opacity: 0.3, blur: 5 };
const DEFAULT_SHADOW: ShadowSettings = { opacity: 0.11, blur: 19, spread: 1.5 };
const DEFAULT_BAR: BarSettings = { letterboxHeight: 63, letterboxOpacity: 0.3, letterboxBlur: 5, letterboxInset: 6.5, separatorPx: 1.5, separatorFade: 1, menuBarHeight: 6, menuBarScale: 1.25, cornerRadius: 6 };

interface ThemeContextValue {
  theme: ThemeName;
  def: ThemeDefinition;
  setTheme: (name: ThemeName) => void;
  cycleTheme: () => void;
  cyclePreviousTheme: () => void;
  glass: GlassSettings;
  setGlass: (settings: GlassSettings) => void;
  shadow: ShadowSettings;
  setShadow: (settings: ShadowSettings) => void;
  bar: BarSettings;
  setBar: (settings: BarSettings) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Write all --wt-* CSS custom properties onto <html> from themes.ts + global settings. */
// Status colours are fixed across themes — they encode meaning, not
// aesthetic. These live in the root but are set per-render so a theme
// override can take over cleanly if needed later.
const STATUS_DANGER = "#b84b48";
const STATUS_WARNING = "#b96e32";
const STATUS_SUCCESS = "#2d8275";

function applyThemeVars(name: ThemeName, glass: GlassSettings, shadow: ShadowSettings) {
  const def = THEMES[name];
  const s = document.documentElement.style;

  // SS-shape runtime vars — the full token surface the ported components
  // and 3D infra (unused in Waytide but preserved for future work) read.
  s.setProperty("--wt-bg", def.bg);
  s.setProperty("--wt-text", def.text);
  s.setProperty("--wt-text-muted", def.textMuted);
  s.setProperty("--wt-accent", def.accent);
  s.setProperty("--wt-glass-rgb", def.glassRgb);
  s.setProperty("--wt-glass", `rgba(${def.glassRgb},${glass.opacity})`);
  s.setProperty("--wt-glass-hover", `rgba(${def.glassRgb},${Math.min(glass.opacity + 0.08, 1)})`);
  s.setProperty("--wt-blur", `${glass.blur}px`);
  s.setProperty("--wt-shadow", `0 0 ${shadow.blur}px ${shadow.spread}px rgba(0,0,0,${shadow.opacity})`);
  s.setProperty("--wt-shadow-hover", `0 0 ${shadow.blur + 1}px ${shadow.spread + 1}px rgba(0,0,0,${Math.min(shadow.opacity * 1.2, 1)})`);
  s.setProperty("--wt-border", def.border);
  s.setProperty("--wt-border-highlight", def.borderHighlight);
  s.setProperty("--wt-separator", def.separator);
  s.setProperty("--wt-selection-bg", def.selection);
  s.setProperty("--wt-selection-text", def.selectionText);
  s.setProperty("--wt-grid-color", def.gridColor);
  s.setProperty("--wt-noise-opacity", def.noiseOpacity);
  s.setProperty("--wt-grid-opacity", def.gridOpacity);
  s.setProperty("--wt-text-shadow", def.textShadow);
  s.setProperty("--wt-axis-x", def.axisXColor);
  s.setProperty("--wt-axis-z", def.axisZColor);

  // Waytide semantic tokens — the set the existing components already
  // consume (bg-surface, text-primary, border-edge, etc.). Derived from
  // the SS-shape ThemeDefinition so all 20 ported themes light up without
  // rewriting the callers. Extra semantic roles Waytide carries but SS
  // does not (surface-raised, faint, edge-strong, danger/warning/success)
  // are computed or fixed.
  s.setProperty("--wt-surface", def.bg);
  s.setProperty("--wt-surface-raised", `color-mix(in srgb, ${def.bg} 92%, white)`);
  s.setProperty("--wt-surface-glass", `rgba(${def.glassRgb},${glass.opacity})`);
  s.setProperty("--wt-primary", def.text);
  s.setProperty("--wt-muted", def.textMuted);
  s.setProperty("--wt-faint", `color-mix(in srgb, ${def.text} 45%, transparent)`);
  s.setProperty("--wt-accent-strong", `color-mix(in srgb, ${def.accent} 80%, black)`);
  s.setProperty("--wt-edge", def.border);
  s.setProperty("--wt-edge-strong", def.borderHighlight);
  s.setProperty("--wt-selection", def.selection);
  s.setProperty("--wt-danger", STATUS_DANGER);
  s.setProperty("--wt-warning", STATUS_WARNING);
  s.setProperty("--wt-success", STATUS_SUCCESS);

  s.setProperty("--wt-font", def.font);
  document.body.style.fontFamily = def.font;
  // Load font import if needed, clean up stale link if switching to a no-import theme
  const existingLink = document.querySelector<HTMLLinkElement>(`link[data-wt-font]`);
  if (def.fontImport) {
    if (!existingLink || existingLink.href !== def.fontImport) {
      if (existingLink) existingLink.remove();
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = def.fontImport;
      link.dataset.wtFont = "true";
      document.head.appendChild(link);
    }
  } else if (existingLink) {
    existingLink.remove();
  }

  // Waytide favicon keeps the wave-in-circle shape from
  // public/favicon.svg but tints the disc to the active theme's
  // accent. Rewritten only when the accent changes.
  updateFavicon(def.accent);
}

let _lastFaviconAccent = "";
function updateFavicon(accent: string) {
  if (typeof document === "undefined") return;
  if (accent === _lastFaviconAccent) return;
  _lastFaviconAccent = accent;
  // Browser-tab favicon uses the same shape as public/favicon.svg
  // (which BrandLogo renders via mask-image), tinted to the active
  // theme's accent colour.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 209.28685 134.98569"><path fill="${accent}" d="M 26.553819,109.54329 C 20.205153,110.71986 7.1144964,57.682913 25.7475,35.517349 43.454029,14.453907 73.313976,11.94052 100.69627,26.484192 130.19681,42.152938 138,62 162,70 c 12.68869,3.965214 20.20352,2.34899 28.88544,-2.83107 7.73781,-4.616757 4.37808,-10.019844 7.22954,-10.663661 C 205.86074,56.477799 214.20888,115 172,125 140,133 109.86018,109.32354 85.21942,88.090704 64.435642,70.181408 50.31341,73.503813 40.759503,81.428302 25.882209,93.768276 31.566725,108.27918 26.553819,109.54329 Z"/></svg>`;
  const blob = new Blob([svg], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  const old = link.href;
  link.type = "image/svg+xml";
  link.href = url;
  if (old.startsWith("blob:")) URL.revokeObjectURL(old);
}

/** Swap theme class on <html> (for FOUC script compatibility). */
function applyThemeClass(name: ThemeName) {
  const root = document.documentElement;
  for (const t of THEME_NAMES) {
    root.classList.remove(THEMES[t].className);
  }
  root.classList.add(THEMES[name].className);
}

function readInitialTheme(): ThemeName {
  if (typeof window === "undefined") return DEFAULT_THEME;
  return migrateThemeName(localStorage.getItem(STORAGE_KEY));
}

function applyAndPersist(name: ThemeName, glass: GlassSettings, shadow: ShadowSettings) {
  applyThemeVars(name, glass, shadow);
  applyThemeClass(name);
  localStorage.setItem(STORAGE_KEY, name);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(readInitialTheme);
  const [glass, setGlassState] = useState<GlassSettings>(DEFAULT_GLASS);
  const [shadow, setShadowState] = useState<ShadowSettings>(DEFAULT_SHADOW);
  const [bar, setBarState] = useState<BarSettings>(DEFAULT_BAR);
  const glassRef = useRef(glass);
  const shadowRef = useRef(shadow);
  const barRef = useRef(bar);
  glassRef.current = glass;
  shadowRef.current = shadow;
  barRef.current = bar;

  const setTheme = useCallback((name: ThemeName) => {
    setThemeState(name);
    applyAndPersist(name, glassRef.current, shadowRef.current);
  }, []);

  const setGlass = useCallback((settings: GlassSettings) => {
    setGlassState(settings);
    glassRef.current = settings;
    applyThemeVars(
      (localStorage.getItem(STORAGE_KEY) ?? DEFAULT_THEME) as ThemeName,
      settings,
      shadowRef.current,
    );
  }, []);

  const setShadow = useCallback((settings: ShadowSettings) => {
    setShadowState(settings);
    shadowRef.current = settings;
    applyThemeVars(
      (localStorage.getItem(STORAGE_KEY) ?? DEFAULT_THEME) as ThemeName,
      glassRef.current,
      settings,
    );
  }, []);

  const setBar = useCallback((settings: BarSettings) => {
    setBarState(settings);
    barRef.current = settings;
    document.documentElement.style.setProperty("--wt-radius", `${settings.cornerRadius}px`);
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState((prev) => {
      const idx = THEME_NAMES.indexOf(prev);
      const next = THEME_NAMES[(idx + 1) % THEME_NAMES.length];
      applyAndPersist(next, glassRef.current, shadowRef.current);
      return next;
    });
  }, []);

  const cyclePreviousTheme = useCallback(() => {
    setThemeState((prev) => {
      const idx = THEME_NAMES.indexOf(prev);
      const prevIdx = (idx - 1 + THEME_NAMES.length) % THEME_NAMES.length;
      const next = THEME_NAMES[prevIdx];
      applyAndPersist(next, glassRef.current, shadowRef.current);
      return next;
    });
  }, []);

  // [ / ] hotkeys to cycle themes (SS parity). Ignored when the user
  // is typing in an input, textarea, select, or contentEditable — so
  // the bracket characters remain usable in forms. Also ignored when
  // a modifier is held (ctrl/meta/alt) so dev-tools shortcuts survive.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? "";
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      if (e.key === "]") {
        e.preventDefault();
        cycleTheme();
      } else if (e.key === "[") {
        e.preventDefault();
        cyclePreviousTheme();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [cycleTheme, cyclePreviousTheme]);

  // Apply theme vars + class on mount.
  useEffect(() => {
    applyThemeVars(theme, glass, shadow);
    applyThemeClass(theme);
  }, [theme, glass, shadow]);

  // Apply global corner radius CSS var.
  useEffect(() => {
    document.documentElement.style.setProperty("--wt-radius", `${bar.cornerRadius}px`);
  }, [bar.cornerRadius]);

  const def = THEMES[theme];

  return (
    <ThemeContext.Provider
      value={{
        theme,
        def,
        setTheme,
        cycleTheme,
        cyclePreviousTheme,
        glass,
        setGlass,
        shadow,
        setShadow,
        bar,
        setBar,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
